import { AnalysisRow } from '../types';

/**
 * 한글 + 숫자 혼합 이름을 자연스럽게 비교한다.
 * numeric: true — 'Line 2'가 'Line 10'보다 앞에 오도록 (사전순이면 10이 먼저다).
 * Collator를 한 번만 만들어 재사용한다 — 행이 수천이라 localeCompare를 매번 부르면 느리다.
 */
const collator = new Intl.Collator('ko', { numeric: true, sensitivity: 'base' });

/** 공통코드에 없는 공정은 맨 뒤로 */
const UNKNOWN_PROC_RANK = Number.MAX_SAFE_INTEGER;

/** 공정 / 설비군 / 세부공정까지의 공통 비교 — 0이면 그 뒤 키로 넘어간다 */
const compareProcessGroup = (
  a: { process: string; eqGroup: string; subProcess: string },
  b: { process: string; eqGroup: string; subProcess: string },
  procOrder: Map<string, number>
): number => {
  const ra = procOrder.get(a.process) ?? UNKNOWN_PROC_RANK;
  const rb = procOrder.get(b.process) ?? UNKNOWN_PROC_RANK;
  if (ra !== rb) return ra - rb;
  // 둘 다 공통코드에 없으면(순번이 같으면) 그들끼리는 이름순으로 둔다
  if (ra === UNKNOWN_PROC_RANK) {
    const c = collator.compare(a.process, b.process);
    if (c !== 0) return c;
  }
  const c = collator.compare(a.eqGroup, b.eqGroup);
  return c !== 0 ? c : collator.compare(a.subProcess, b.subProcess);
};

/**
 * ⑤ 세부 Summary처럼 공정/설비군/세부공정 단위로 묶인 행 정렬.
 *
 * 타입을 좁히지 않고 최소 형태만 받는다 — analyze.ts의 DetailGroup을 import하면
 * analyze.ts ↔ sortRows.ts가 서로를 참조하게 된다.
 */
export const sortByProcessGroup = <
  T extends { process: string; eqGroup: string; subProcess: string }
>(
  items: T[],
  procOrder: Map<string, number>
): T[] => [...items].sort((a, b) => compareProcessGroup(a, b, procOrder));

/**
 * ④ 비교 분석 표의 행 정렬 — 공정 / 설비군 / 세부공정 / Machine / 라인 순.
 *
 * 공정만 이름순이 아니라 **공통코드(PRDN_PROC_CD) 등록 순서**를 따른다. 공정은 실제 생산
 * 흐름 순서가 의미를 갖는데 이름순으로 늘어놓으면 그 순서가 깨지기 때문이다.
 * 순서는 usePrdnProcOrder()가 `공정명 → 순번`으로 준다.
 *
 * 1라인별 모드에서는 고정열이 `… / Main / Machine`으로 바뀌므로 마지막 두 키도 그에 맞춘다.
 *
 * 원본 배열은 건드리지 않는다 — 스토어의 analysisResult를 그대로 받는 호출부가 있다.
 */
export const sortAnalysisRows = (
  rows: AnalysisRow[],
  procOrder: Map<string, number>,
  isPerLineMode: boolean
): AnalysisRow[] => {
  return [...rows].sort((a, b) => {
    const c = compareProcessGroup(a, b, procOrder);
    if (c !== 0) return c;

    if (isPerLineMode) {
      const m = collator.compare(a.main, b.main);
      return m !== 0 ? m : collator.compare(a.machine, b.machine);
    }
    const m = collator.compare(a.machine, b.machine);
    return m !== 0 ? m : collator.compare(a.line, b.line);
  });
};
